import React from "react";
import {
  FaPercentage,
  FaClipboard,
  FaCalendarAlt,
  FaRedoAlt,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import {
  MdAttachMoney,
  MdShoppingCart,
  MdInfoOutline,
} from "react-icons/md";

const PromocodeInfo = ({ promoData }) => {
  const handleCopyPromoCode = () => {
    navigator.clipboard.writeText(promoData?.promo_code || "");
    // You can replace alert with a toast notification in real app
    alert("Promo code copied to clipboard!");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "N/A";
    return `$${parseFloat(value.$numberDecimal || value).toFixed(2)}`;
  };

  return (
    <div className="w-full px-4">
      {/* Header: Promo Code + Status + Copy Button */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50  rounded-2xl p-6 mb-8 shadow-sm border border-blue-100 dark:border-blue-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-blue-500 mb-2">
              {promoData?.promo_code || "N/A"}
            </h2>
            <div className="flex items-center gap-3">
              {promoData?.active ? (
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-700  font-medium">
                  <FaCheckCircle />
                  Active
                </span>
              ) : (
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 text-red-700  font-medium">
                  <FaTimesCircle />
                  Inactive
                </span>
              )}
              {promoData?.description && (
                <p className="text-gray-600 dark:text-gray-400 italic">
                  "{promoData.description}"
                </p>
              )}
            </div>
          </div>

          <button
            onClick={handleCopyPromoCode}
            className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            <FaClipboard />
            Copy Code
          </button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-5">
          <InfoItem
            icon={<FaPercentage className="text-blue-600" />}
            label="Discount"
            value={`${promoData?.discount?.$numberDecimal || promoData?.discount || 0}%`}
          />

          <InfoItem
            icon={<MdAttachMoney className="text-green-600" />}
            label="Discount Type"
            value={promoData?.discount_type || "N/A"}
          />

          <InfoItem
            icon={<MdShoppingCart className="text-purple-600" />}
            label="Applicable For"
            value={promoData?.promo_for || "All Orders"}
          />

          <InfoItem
            icon={<FaCalendarAlt className="text-indigo-600" />}
            label="Start Date"
            value={formatDate(promoData?.start_date)}
          />

          <InfoItem
            icon={<FaCalendarAlt className="text-orange-600" />}
            label="End Date"
            value={formatDate(promoData?.end_date)}
          />

          <InfoItem
            icon={<FaRedoAlt className="text-teal-600" />}
            label="Repeat Allowed"
            value={promoData?.repeat ? "Yes" : "No"}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-5">
          <InfoItem
            icon={<MdShoppingCart className="text-pink-600" />}
            label="Apply After Orders"
            value={promoData?.apply_offer_after_orders ?? "0"}
          />

          <InfoItem
            icon={<FaPercentage className="text-cyan-600" />}
            label="Required Previous Uses"
            value={promoData?.required_uses ?? "0"}
          />

          <InfoItem
            icon={<MdShoppingCart className="text-amber-600" />}
            label="Min Items in Cart"
            value={promoData?.min_item_on_cart ?? "1"}
          />

          <InfoItem
            icon={<MdAttachMoney className="text-emerald-600" />}
            label="Minimum Order Amount"
            value={formatCurrency(promoData?.min_order_amount)}
          />

          <InfoItem
            icon={<MdAttachMoney className="text-rose-600" />}
            label="Maximum Discount"
            value={formatCurrency(promoData?.max_discount_amount)}
          />
        </div>
      </div>

      {/* Optional: Description or Note */}
      {promoData?.note && (
        <div className="mt-8 p-5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex items-start gap-3">
          <MdInfoOutline className="text-blue-500 text-xl mt-0.5" />
          <p className="text-gray-700 dark:text-gray-300">{promoData.note}</p>
        </div>
      )}
    </div>
  );
};

// Reusable Info Item Component for clean code
const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 p-4 bg-gray-50  rounded-xl hover:bg-gray-100 border transition-all">
    <div className="text-2xl">{icon}</div>
    <div className="flex-1">
      <p className="text-sm text-gray-600 ">{label}</p>
      <p className="text-lg font-semibold text-gray-900 ">
        {value}
      </p>
    </div>
  </div>
);

export default PromocodeInfo;